import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { axiosNewInstance } from "../../config/Api";
import LangData from "../common/LangData";

const REFERENCE_ID = "1";

const CustomerDetails = () => {
  const history = useHistory();
  const token = localStorage.getItem("authToken");
  const mobile = useSelector((state) => state?.user?.loginuser?.mobile) || "";
  const roleId = useSelector((state) => state?.user?.loginuser?.role_id);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [selectedPolicyNo, setSelectedPolicyNo] = useState(null);
  const [searchPolicyNo, setSearchPolicyNo] = useState("");
  const [agentInfo, setAgentInfo] = useState(null);
  const [agencyLoading, setAgencyLoading] = useState(false);

  // Filter state
  const [filterPolicyNo, setFilterPolicyNo] = useState("");
  const [filterName, setFilterName] = useState("");

  const fetchData = useCallback(async (overrideMobile = null) => {
    if (!token) {
      history.push("/login");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setAgentInfo(null);

    const mobileNo = (mobile || "").toString().trim().replace(/\D/g, "");
    const searchMobile = overrideMobile || mobileNo;

    try {
      let agencyCode = "";

      if (Number(roleId) === 4 || Number(roleId) === 2) {
        // Agent or LIC Employee: Needs to fetch agency code first
        if (!searchMobile || searchMobile.length < 10) {
          setError(Number(roleId) === 2 ? "Please enter an agent mobile number." : "Mobile number not found or invalid.");
          setLoading(false);
          return;
        }

        const agentRes = await axiosNewInstance.get(
          `api/agentEnquiryLogin/${encodeURIComponent(searchMobile)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        const agentData = agentRes?.data;
        if (agentData?.Error) {
          setError(agentData.Error);
          setLoading(false);
          return;
        }
        setAgentInfo(agentData);
        agencyCode = agentData?.agencyCode || "";
        if (!agencyCode) {
          setError("Agency code not found for this mobile number.");
          setLoading(false);
          return;
        }
      }

      const isCustomer = Number(roleId) === 5;
      const endpoint = isCustomer ? "api/customerDetailsCustomer" : "api/customerDetails";

      const payload = isCustomer
        ? { referenceId: "5", mobileNo: searchMobile }
        : { referenceId: REFERENCE_ID, agentId: agencyCode };

      const response = await axiosNewInstance.post(
        endpoint,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setResult(response?.data ?? null);
    } catch (err) {
      const message =
        err?.response?.data?.Error ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to fetch customer details.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token, mobile, history, roleId]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      history.push("/login");
      return;
    }

    // Auto-fetch for Roles 4 (Agent) and 5 (Customer)
    if (Number(roleId) === 4 || Number(roleId) === 5) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [token, roleId, fetchData]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (Number(roleId) === 2) {
      if (!searchPolicyNo.trim()) {
        setError("Please enter a Policy Number.");
        return;
      }
      setLoading(true);
      setError(null);
      setResult(null);
      setAgentInfo(null);
      try {
        const response = await axiosNewInstance.post(
          `api/policyDetails/${encodeURIComponent(searchPolicyNo.trim())}`,
          null,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        // Transform single policy object into an array for renderResult compatibility
        const data = response?.data;
        if (data?.Error) {
          setError(data.Error);
        } else {
          setResult(data);
        }
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Unable to fetch policy details.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenDetails = async (policyNo) => {
    if (!token) {
      history.push("/login");
      return;
    }
    setSelectedPolicyNo(policyNo);
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetailsData(null);
    try {
      const response = await axiosNewInstance.post(
        `api/policyDetails/${encodeURIComponent(policyNo)}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      setDetailsData(response?.data ?? null);
    } catch (err) {
      const msg =
        err?.response?.data?.Error ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to fetch policy details.";
      setDetailsError(msg);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsError(null);
    setDetailsData(null);
    setSelectedPolicyNo(null);
  };

  const renderResult = () => {
    if (!result) return null;

    const isLicEmployee = Number(roleId) === 2;

    if (isLicEmployee) {
      // Show full details inline for LIC Employee
      return (
        <div className="table-responsive mt-3">
          <table className="table table-sm table-bordered">
            <tbody>
              {Object.entries(result).map(([key, value]) => (
                <tr key={key}>
                  <th scope="row" style={{ width: "30%", background: "#f8f9fa", textTransform: "none" }}>
                    {key}
                  </th>
                  <td>
                    {typeof value === "object" && value !== null
                      ? JSON.stringify(value)
                      : String(value ?? "N/A")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    const policyDetails =
      result?.policyDetails ??
      result?.policy_details ??
      result?.data?.policyDetails ??
      result?.data?.policy_details;
    const rows = Array.isArray(policyDetails) ? policyDetails : [];

    if (rows.length === 0) {
      return (
        <div className="table-responsive mt-3">
          <p className="text-muted mb-0">No policy details found.</p>
          <pre className="p-3 bg-light rounded small mb-0 mt-2">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      );
    }

    // Apply filters
    const filteredRows = rows.filter((row) => {
      const matchPolicy = filterPolicyNo
        ? String(row.policyNo ?? "").toLowerCase().includes(filterPolicyNo.toLowerCase())
        : true;
      const matchName = filterName
        ? String(row.name ?? "").toLowerCase().includes(filterName.toLowerCase())
        : true;
      return matchPolicy && matchName;
    });

    const columns = [
      { key: "policyNo", labelKey: "label_policy_no" },
      { key: "name", labelKey: "label_name" },
      { key: "doc", labelKey: "label_date_of_c" },
      { key: "premiumAmount", labelKey: "label_premium_amount_col" },
      { key: "policyStatus", labelKey: "label_policy_status" },
    ];

    return (
      <>
        {/* Filter Row */}
        <div className="row mb-3 mt-3">
          <div className="col-md-4">
            <label className="font-weight-bold mb-1">
              <LangData keyname="label_policy_no" />
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Policy No"
              value={filterPolicyNo}
              onChange={(e) => setFilterPolicyNo(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="font-weight-bold mb-1">
              <LangData keyname="label_name" />
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
          </div>
        </div>

        <div className="table-responsive mt-3" style={{ height: "500px", overflowY: "auto" }}>
          <table className="table text-left">
            <thead>
              <tr>
                <th scope="col">
                  <LangData keyname="label_serial" />
                </th>
                {filteredRows.length > 0 && Object.keys(filteredRows[0]).map((col) => (
                  <th key={col} scope="col" style={{ textTransform: "none" }}>
                    {col}
                  </th>
                ))}
                <th scope="col">
                  <LangData keyname="label_action" />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length > 0 ? (
                filteredRows.map((row, index) => (
                  <tr key={row.policyNo || index}>
                    <td>{index + 1}</td>
                    {Object.keys(row).map((col) => (
                      <td key={col}>{String(row[col] ?? "")}</td>
                    ))}
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => handleOpenDetails(row.policyNo)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="100%" className="text-center text-muted">
                    No matching records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div className="container">
      <div className="row g-0">
        <div className="col-md-12 pr-2 mt-4">
          <div className="card p-3 shadow-sm">
            <h5 className="font-weight-bold fs-4 my-3">
              <LangData keyname="customer_details" />
            </h5>



            {/* Manual Search for LIC Employee (Role 2) */}
            {Number(roleId) === 2 && (
              <form onSubmit={handleSearch} className="mb-4">
                <div className="row align-items-end">
                  <div className="col-md-4">
                    <label className="font-weight-bold mb-1">Policy Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Policy Number"
                      value={searchPolicyNo}
                      onChange={(e) => setSearchPolicyNo(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Searching..." : "Search"}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Agent Info Card — for Agent and Employee roles */}
            {(Number(roleId) === 4 || Number(roleId) === 2) && agentInfo && (
              <div className="card mb-3" style={{ background: "#f0f7ff", border: "1px solid #b3d4fc" }}>
                <div className="card-body py-2 px-3">
                  <div className="row">
                    <div className="col-md-4 mb-1">
                      <small className="text-muted d-block">Agent Name</small>
                      <strong>{agentInfo.agentName || "N/A"}</strong>
                    </div>
                    <div className="col-md-2 mb-1">
                      <small className="text-muted d-block">Agency Code</small>
                      <strong>{agentInfo.agencyCode || "N/A"}</strong>
                    </div>
                    <div className="col-md-2 mb-1">
                      <small className="text-muted d-block">Agent Type</small>
                      <strong>{agentInfo.agentType || "N/A"}</strong>
                    </div>
                    <div className="col-md-2 mb-1">
                      <small className="text-muted d-block">Email</small>
                      <strong>{agentInfo.agentEmail || "N/A"}</strong>
                    </div>
                    <div className="col-md-2 mb-1">
                      <small className="text-muted d-block">Mobile</small>
                      <strong>{agentInfo.agentMobileNo || "N/A"}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading && (
              <p className="text-muted mb-0">
                <span className="spinner-border spinner-border-sm mr-1" />
                Loading customer details...
              </p>
            )}
            {error && (
              <div className="alert alert-danger py-2" role="alert">
                {error}
              </div>
            )}
            {!loading && result && renderResult()}
          </div>
        </div>
      </div>
      {/* Policy Details Modal + Backdrop */}
      {detailsOpen && (
        <>
          {/* Overlay / Backdrop */}
          <div className="modal-backdrop fade show"></div>

          {/* Modal */}
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="policyDetailsModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="policyDetailsModalLabel">
                    Policy Details {selectedPolicyNo ? `- ${selectedPolicyNo}` : ""}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={handleCloseDetails}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {detailsLoading && (
                    <p className="text-muted mb-0">
                      <span className="spinner-border spinner-border-sm mr-1" />
                      Loading policy details...
                    </p>
                  )}
                  {detailsError && (
                    <div className="alert alert-danger py-2" role="alert">
                      {detailsError}
                    </div>
                  )}
                  {!detailsLoading && detailsData && (
                    <div className="table-responsive">
                      <table className="table table-sm table">
                        <tbody>
                          {Object.entries(detailsData).map(([key, value]) => (
                            <tr key={key}>
                              <th scope="row" style={{ width: "30%", textTransform: "none" }}>
                                {key}
                              </th>
                              <td>
                                {typeof value === "object" && value !== null
                                  ? JSON.stringify(value)
                                  : String(value ?? "")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerDetails;
